import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { resources } from '../../core/services/resources';
import { updateViewContext } from '../../core/services/viewContext';
import { PresentationModeDialog } from './PresentationModeDialog';
import { useVisualizationImage } from './useVisualizationImage';
import type {
  DocumentPresentation,
  DocumentPresentationSlide,
} from '../../core/types/data-models';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString();
}

const SLIDE_TYPE_LABEL: Record<DocumentPresentationSlide['slideType'], string> = {
  COVER: 'Cover',
  OVERVIEW: 'Overview',
  SECTION: 'Section',
  GROUP: 'Group',
  RELATIONS: 'Relations',
  SUMMARY: 'Summary',
};

function SlideRow({ slide, onClick }: { slide: DocumentPresentationSlide; onClick: () => void }) {
  const { src, isLoading } = useVisualizationImage(slide.documentVisualizationId);

  return (
    <Paper
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        gap: 2,
        p: 1.5,
        width: '100%',
        textAlign: 'left',
        border: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Box
        sx={{
          width: 200,
          height: 112,
          flexShrink: 0,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {slide.documentVisualizationId == null ? (
          <ImageOutlinedIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
        ) : isLoading || !src ? (
          <Skeleton variant="rectangular" width={200} height={112} />
        ) : (
          <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
      </Box>

      <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" color="text.secondary">
            {slide.orderIndex + 1}
          </Typography>
          <Chip
            label={SLIDE_TYPE_LABEL[slide.slideType] ?? slide.slideType}
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: 10 }}
          />
        </Stack>
        <Typography sx={{ fontWeight: 700, fontSize: 17, letterSpacing: '-0.01em' }}>
          {slide.title}
        </Typography>
        {slide.body && (
          <Box
            sx={{
              fontSize: 'var(--font-size-sm)',
              color: 'text.secondary',
              '& ul,& ol': { pl: 2.5, m: 0, display: 'flex', flexDirection: 'column', gap: '2px' },
              '& p': { m: 0 },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{slide.body}</ReactMarkdown>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

/**
 * The presentation viewer: every slide of the selected presentation as a row,
 * with a way back to the document. Clicking a slide starts the slide show.
 */
export function DocumentPresentationsPage() {
  const { id } = useParams<{ id: string }>();
  const docId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [slideShowIndex, setSlideShowIndex] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const presentationsQuery = useQuery({
    queryKey: ['document-presentations', docId],
    queryFn: () =>
      resources.documentPresentations.list({
        filter: { documentId: [docId] },
        sort: '-createdAt',
        pageSize: 100,
      }),
    enabled: Number.isFinite(docId),
    // A presentation being generated flips to READY in the background.
    refetchInterval: (query) =>
      query.state.data?.data.some((p) => p.status === 'IN_GENERATION') ? 5000 : false,
  });

  const docQuery = useQuery({
    queryKey: ['documents', docId],
    queryFn: () => resources.documents.get(docId),
    enabled: Number.isFinite(docId),
  });

  // Let the AI Assistant show this document's name once it loads.
  useEffect(() => {
    const doc = docQuery.data;
    if (doc) updateViewContext({ documentId: doc.id, documentName: doc.name, projectId: doc.projectId });
  }, [docQuery.data]);

  const presentations: DocumentPresentation[] = presentationsQuery.data?.data ?? [];
  const currentId = selectedId ?? presentations[0]?.id ?? null;
  const current = presentations.find((p) => p.id === currentId) ?? null;

  const detailQuery = useQuery({
    queryKey: ['document-presentation', currentId],
    queryFn: () => resources.getDocumentPresentation(currentId!),
    enabled: currentId != null,
    // While it is still being generated the slides arrive one by one.
    refetchInterval: current?.status === 'IN_GENERATION' ? 5000 : false,
  });

  const slides = detailQuery.data?.slides ?? [];

  const handleDelete = async () => {
    if (currentId == null) return;
    setDeleting(true);
    try {
      await resources.documentPresentations.remove(currentId);
      await queryClient.invalidateQueries({ queryKey: ['document-presentations', docId] });
      setSelectedId(null);
      setDeleteConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  if (presentationsQuery.isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton width={280} height={40} />
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={130} sx={{ borderRadius: 1 }} />
        ))}
      </Stack>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 2, pb: 3 }}>
      {/* ── Header ── */}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/documents/${docId}`)}
        >
          Back to document
        </Button>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 26,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {current?.title ?? docQuery.data?.name ?? 'Presentation'}
          </Typography>
        </Box>
        {slides.length > 0 && (
          <Button
            size="small"
            variant="contained"
            startIcon={<SlideshowIcon />}
            onClick={() => setSlideShowIndex(0)}
          >
            Play
          </Button>
        )}
        {current && (
          <Tooltip title="Delete presentation">
            <span>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                Delete
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      {/* ── Presentation picker (only with more than one) ── */}
      {presentations.length > 1 && (
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
          {presentations.map((p) => (
            <Chip
              key={p.id}
              label={formatDate(p.createdAt)}
              size="small"
              clickable
              color={p.id === currentId ? 'primary' : 'default'}
              variant={p.id === currentId ? 'filled' : 'outlined'}
              onClick={() => setSelectedId(p.id)}
              icon={p.status === 'IN_GENERATION' ? <CircularProgress size={12} sx={{ ml: 1 }} /> : undefined}
            />
          ))}
        </Stack>
      )}

      <Divider />

      {/* ── Slides ── */}
      {current?.status === 'IN_GENERATION' && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            This presentation is still being generated — slides appear as they are ready.
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {current?.status === 'FAILED' && (
        <Typography variant="body2" color="error">
          Generating this presentation failed. Try generating a new one from the document.
        </Typography>
      )}

      {presentations.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          This document has no presentations yet.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {slides.map((slide, index) => (
            <SlideRow key={slide.id} slide={slide} onClick={() => setSlideShowIndex(index)} />
          ))}
          {slides.length === 0 && detailQuery.isFetched && current?.status !== 'IN_GENERATION' && (
            <Typography variant="body2" color="text.secondary">
              This presentation has no slides.
            </Typography>
          )}
        </Stack>
      )}

      {/* ── Slide show ── */}
      <PresentationModeDialog
        open={slideShowIndex != null}
        onClose={() => setSlideShowIndex(null)}
        slides={slides}
        index={slideShowIndex ?? 0}
        onIndexChange={setSlideShowIndex}
      />

      {/* ── Delete confirmation ── */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete presentation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The presentation and the images generated for it will be permanently deleted.
            Section visualizations it reused are kept.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" size="small" disabled={deleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
